// src/design-system/LineChart.tsx
import { useId } from "react";

export type Point = { label: string; value: number; alert?: boolean };

/**
 * رسم القراءات بنفس هندسة التصميم: viewBox 320×130، خط 2.5 مضيء، نقاط 3.5، والشاذة حمراء بقطر 6،
 * وتحتها صف قيم بخط mono 11px. المدى الرأسي 20←98 كما في النموذج. `area` يضيف تعبئة متدرّجة مضيئة
 * تحت الخط (لعرض اتجاه عام كإيرادات شهرية) بدون أي تغيير على الاستخدام الحالي بدونه.
 */
export function LineChart({
  points, color = "#F0CE8A", glow = "rgba(214,172,85,.8)", height = 136, label, area = false,
}: { points: Point[]; color?: string; glow?: string; height?: number; label: string; area?: boolean }) {
  if (points.length === 0) return null;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals);
  const span = Math.max(Math.max(...vals) - min, 1);
  const x = (i: number) => (points.length === 1 ? 160 : (i * 320) / (points.length - 1));
  const y = (v: number) => 98 - ((v - min) / span) * 78;
  const line = points.map((p, i) => `${+x(i).toFixed(1)},${+y(p.value).toFixed(1)}`).join(" ");
  const gradId = useId();

  return (
    <div role="img" aria-label={label}>
      <svg viewBox="0 0 320 130" width="100%" height={height} preserveAspectRatio="none" style={{ overflow: "visible", direction: "ltr" }}>
        {area && (
          <>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.35" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>
            <polygon points={`${x(0)},98 ${line} ${x(points.length - 1)},98`} fill={`url(#${gradId})`} stroke="none" />
          </>
        )}
        <polyline points={line} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" style={{ filter: `drop-shadow(0 0 9px ${glow})` }} />
        {points.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.value)} r={p.alert ? 6 : 3.5} fill={p.alert ? "#F0736A" : color} data-alert={p.alert || undefined}
            style={p.alert ? { filter: "drop-shadow(0 0 14px rgba(240,115,106,.95))" } : undefined} />
        ))}
      </svg>
      <div className="mono ltr flex justify-between text-[11px] text-muted" style={{ unicodeBidi: "isolate" }}>
        {points.map((p, i) => <span key={i} style={p.alert ? { color: "#F0736A" } : undefined}>{p.label}</span>)}
      </div>
    </div>
  );
}
